import { useMemo } from 'react';

import { Message, MessageProps } from '@/components/Message';
import { MessageUnionType, RunOutputSchema, RunSchema } from '@/types/schema';
import {
  getFlattenedArrayOfGenerations,
  getMessageContent,
  getMessageContentAsText,
  getMessageFields,
  getMessageType,
  isMessageLike,
  maybeGetArrayOfMessages,
} from '@/utils/messages';

function getMessageFromInput(input: unknown): MessageUnionType | undefined {
  if (!input) return undefined;
  if (isMessageLike(input)) return input;
  const maybeMessagesArray = maybeGetArrayOfMessages(input);
  if (maybeMessagesArray) {
    return maybeMessagesArray.at(-1);
  }

  const singleInputKey = Object.keys(input).length === 1;
  const singleKey = singleInputKey ? Object.keys(input)[0] : undefined;

  if (singleKey) {
    return getMessageFromInput(input[singleKey]);
  }

  const messageListKey = Object.keys(input).find(
    (key) => isMessageLike(input[key]) || maybeGetArrayOfMessages(input[key])
  );
  if (messageListKey) {
    return getMessageFromInput(input[messageListKey]);
  }

  // LLM runs might contain the message somewhere else
  // @see smith-frontend/src/Pages/Run/components/RunDetailsLLM.tsx
  if (
    typeof input === 'object' &&
    input != null &&
    'generations' in input &&
    Array.isArray(input.generations)
  ) {
    const potentialMessageArray = getFlattenedArrayOfGenerations(
      input as RunOutputSchema
    ).map((i: unknown) => {
      if (i == null || typeof i !== 'object' || !('message' in i)) return null;
      return i.message;
    });

    return getMessageFromInput(potentialMessageArray);
  }

  return undefined;
}

export function FoldedMessageList(props: {
  messages: MessageUnionType[] | undefined;
  attachments?: RunSchema['outputs_s3_urls'];
  foldDistractions?:
    | { inputs: RunSchema['inputs'] }
    | { prevRun: RunSchema | undefined };
  collapsedList?: boolean[];
  onCollapse?: (index: number, collapsed: boolean) => void;
  mergeProps?: (
    message: MessageUnionType,
    index: number
  ) => Partial<Omit<MessageProps, 'kwargs'>>;
}) {
  const messages = props.messages;
  const inputMessage = useMemo(() => {
    if (!props.foldDistractions) return undefined;
    if ('inputs' in props.foldDistractions) {
      return getMessageFromInput(props.foldDistractions.inputs);
    }

    if ('prevRun' in props.foldDistractions) {
      return (
        getMessageFromInput(props.foldDistractions.prevRun?.outputs) ||
        getMessageFromInput(props.foldDistractions.prevRun?.inputs)
      );
    }

    return undefined;
  }, [props.foldDistractions]);
  if (!messages) return null;

  let foldIdx = -1;

  if (inputMessage) {
    // find out the last index the message is in the list
    const idx = messages
      .slice()
      .reverse()
      .findIndex(
        (message) =>
          getMessageType(message) === getMessageType(inputMessage) &&
          getMessageContentAsText(message) ===
            getMessageContentAsText(inputMessage)
      );

    if (idx >= 0) {
      foldIdx = messages.length - idx;
    }
  }

  if (foldIdx === -1) {
    return (
      <div className="flex max-w-full flex-col gap-4 has-[[data-virtuoso-scroller]]:w-full">
        {messages.map((message, i) => (
          <Message
            {...(props.mergeProps ? props.mergeProps(message, i) : null)}
            key={i}
            role={getMessageType(message) ?? 'HUMAN'}
            content={getMessageContent(message) ?? ''}
            fields={getMessageFields(message)}
            name={getMessageFields(message)?.name}
            attachments={props.attachments}
            collapsed={props.collapsedList?.[i] ?? false}
            onCollapse={
              typeof props.onCollapse === 'function'
                ? (collapsed) => props.onCollapse?.(i, collapsed)
                : undefined
            }
            onEditKwargs={undefined}
          />
        ))}
      </div>
    );
  }

  return (
    <div className="flex max-w-full flex-col gap-4 has-[[data-virtuoso-scroller]]:w-full">
      {messages.slice(foldIdx).map((message, i) => (
        <Message
          {...(props.mergeProps ? props.mergeProps(message, i) : null)}
          key={i}
          onEditKwargs={undefined}
          role={getMessageType(message) ?? 'HUMAN'}
          content={getMessageContent(message) ?? ''}
          fields={getMessageFields(message)}
          name={getMessageFields(message)?.name}
          attachments={props.attachments}
        />
      ))}
    </div>
  );
}
